import { useMemo, useRef, useState } from "react";
import { Alert, Button } from "react-bootstrap";
import { GridApi, GridReadyEvent } from "ag-grid-community";
import { LoadingIndicator } from "@nkr/components/loading-indicator";
import { IHardToMatchPair } from "@nkr/models/hard-to-match/hard-to-match-pair";
import useExportHardToMatchPairs from "@nkr/store/hard-to-match/hooks/use-export-hard-to-match-pairs";
import useHardToMatchPairs from "@nkr/store/hard-to-match/hooks/use-hard-to-match-pairs";
import EditReviewModal from "./edit-review-modal";
import FilterBar, { defaultHardToMatchFilters, IHardToMatchFilters } from "./filter-bar";
import Grid from "./grid";
import { styles } from "./styles";

const matchesSearch = (pair: IHardToMatchPair, search: string): boolean => {
    const term = search.trim().toLowerCase();

    if (term == "") {
        return true;
    }

    return [
        pair.recipientAlias,
        pair.recipientCenterAlias,
        pair.donorAlias,
        pair.donorCenterAlias,
        pair.avoidsList,
    ].some((value) => value != null && value.toLowerCase().includes(term));
};

const HardToMatch = () => {
    const [filters, setFilters] = useState<IHardToMatchFilters>(defaultHardToMatchFilters);
    const [editingPair, setEditingPair] = useState<IHardToMatchPair>(null);

    const { list, loading, error, saveReview } = useHardToMatchPairs();
    const { onGridReady, exportHardToMatchPairs } = useExportHardToMatchPairs();

    const gridApiRef = useRef<GridApi>(null);

    const handleGridReady = (e: GridReadyEvent) => {
        gridApiRef.current = e.api;
        onGridReady(e);
    };

    const handleReset = () => {
        setFilters(defaultHardToMatchFilters);
        gridApiRef.current?.setFilterModel(null);
    };

    const filteredList = useMemo(
        () =>
            list.filter(
                (x) =>
                    (filters.recipientCenterId == 0 ||
                        x.recipientCenterId == filters.recipientCenterId) &&
                    (filters.donorCenterId == 0 || x.donorCenterId == filters.donorCenterId) &&
                    (filters.recipientBloodType == "" ||
                        x.recipientBloodType == filters.recipientBloodType) &&
                    (filters.showHidden || !x.isHidden) &&
                    matchesSearch(x, filters.search),
            ),
        [list, filters],
    );

    return (
        <>
            <div style={styles.titleRow}>
                <div style={styles.title}>Hard-to-Match Dashboard</div>
                <Button
                    bsSize="sm"
                    disabled={filteredList.length == 0}
                    onClick={exportHardToMatchPairs}
                >
                    Export to CSV
                </Button>
            </div>

            <FilterBar data={list} filters={filters} onChange={setFilters} onReset={handleReset} />

            {error != "" && (
                <Alert bsStyle="danger" style={styles.errorAlert}>
                    {error}
                </Alert>
            )}

            <LoadingIndicator type="spin" color="black" loading={loading} />

            <div style={loading ? styles.gridHidden : null}>
                <Grid
                    rowData={filteredList}
                    onGridReady={handleGridReady}
                    onEdit={setEditingPair}
                />
            </div>

            <EditReviewModal
                pair={editingPair}
                onSave={saveReview}
                onClose={() => setEditingPair(null)}
            />
        </>
    );
};

export default HardToMatch;
